import { api } from '@/lib/api';
import type { VendorModuleType } from '@/features/availability/types';

/**
 * The vendor's own calendar — what their availability rules plus real bookings actually produced
 * on each date, history included.
 *
 * Distinct from the availability feature, which edits the RULES that generate capacity. That one
 * has no concept of a past date; this one is mostly about them.
 */

export type BookingStatus =
  | 'PENDING_PAYMENT'
  | 'AWAITING_VENDOR'
  | 'CONFIRMED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW';

/** How to colour a cell. Precedence is decided server-side; do not re-derive it. */
export type DayTone = 'blackout' | 'booked' | 'open' | 'closed';

export type ScheduleDay = {
  date: string;
  weekday: number;
  is_today: boolean;
  is_past: boolean;
  is_blackout: boolean;
  has_availability: boolean;
  tone: DayTone;
  counts: Partial<Record<BookingStatus, number>>;
  booking_count: number;
  /** Vendor take-home from COMPLETED bookings only, net of the platform cut. */
  earnings_inr: string;
  blocked_slot_count: number;
  /**
   * The day's opening hours, pre-reduced server-side to what fits in a calendar cell.
   *
   * Null means no hours at all on that date. `window_count > 1` means the opens/closes pair
   * spans a gap — the cell says so rather than implying a continuous block.
   */
  hours: {
    opens_at: string;
    closes_at: string;
    window_count: number;
    modules: VendorModuleType[];
    max_concurrent: number;
  } | null;
  /** Earliest LIVE booking of the day. Cancellations do not count - nobody has to turn up. */
  first_booking_at: string | null;
};

export type ScheduleMonth = {
  month: string;
  modules: VendorModuleType[];
  module_type: VendorModuleType | null;
  days: ScheduleDay[];
  totals: {
    booking_count: number;
    earnings_inr: string;
    completed: number;
    cancelled: number;
    no_show: number;
    open_days: number;
    blackout_days: number;
  };
};

export type ScheduleBooking = {
  id: string;
  status: BookingStatus;
  module_type: VendorModuleType;
  slot_start: string | null;
  slot_end: string | null;
  booked_at: string;
  service: { id: string; name: string; duration_minutes: number | null } | null;
  pet: { id: string; name: string; species: string } | null;
  customer: { id: string; name: string | null; phone: string } | null;
  pickup_address: string | null;
  price_inr: string;
  /** What the vendor is paid. Less than price_inr — on services the platform cut is deducted. */
  vendor_earnings_inr: string;
  payment_status: string | null;
  refunded_inr: string | null;
  vendor_notes: string | null;
  cancel_reason: string | null;
  cancelled_by: string | null;
};

export type ScheduleDayDetail = {
  date: string;
  weekday: number;
  is_today: boolean;
  is_past: boolean;
  modules: VendorModuleType[];
  module_type: VendorModuleType | null;
  blackouts: Array<{ id: string; module_type: VendorModuleType; reason: string | null }>;
  blocked_slots: Array<{ id: string; module_type: VendorModuleType; slot_start: string; reason: string | null }>;
  windows: Array<{
    id: string;
    module_type: VendorModuleType;
    start_time: string;
    end_time: string;
    capacity: number;
    /** Whether it came from the weekly pattern or a one-off dated rule. */
    source: 'weekday' | 'date';
  }>;
  bookings: ScheduleBooking[];
};

export async function fetch_schedule_month(params: {
  month: string;
  module_type?: VendorModuleType | '';
}): Promise<ScheduleMonth> {
  const res = await api.get<ScheduleMonth>('/vendor/schedule', {
    params: { month: params.month, module_type: params.module_type || undefined },
  });
  return res.data;
}

export async function fetch_schedule_day(params: {
  date: string;
  module_type?: VendorModuleType | '';
}): Promise<ScheduleDayDetail> {
  const res = await api.get<ScheduleDayDetail>('/vendor/schedule/day', {
    params: { date: params.date, module_type: params.module_type || undefined },
  });
  return res.data;
}
